"use strict";
// An option's own choices, stored in hig_scopeoption.hig_scope (JSON, v4 —
// docs/SCHEMA.md "Option pricing JSON"). Sparse: only what the option sets;
// everything else comes from the measure. Pure functions only — every edit
// returns a new document, and leaves nothing behind when a value is cleared.
Object.defineProperty(exports, "__esModule", { value: true });
exports.paintsOf = exports.removeAreaLine = exports.updateAreaLine = exports.addAreaLine = exports.setConsumablesRate = exports.setWashRate = exports.setAreaWashOnly = exports.setAreaWash = exports.setWashArea = exports.setScaffoldMoves = exports.setSurfaceExcluded = exports.setSurfaceSetting = exports.setAreaShare = exports.setAreaExcluded = exports.serialiseScope = exports.emptyScope = void 0;
exports.parseScope = parseScope;
exports.withSurface = withSurface;
exports.areaWash = areaWash;
exports.addPaint = addPaint;
exports.removePaint = removePaint;
exports.storedPaint = storedPaint;
exports.setSurfacePaint = setSurfacePaint;
exports.setRate = setRate;
/**
 * v2 (#419): an area's sundries are a list of lines. v1 stored one { items, minutes, spread? } and is read as one line.
 * v3 (#510): an area's `wash` / `washOnly` can be false (off for this option although the site ticked it). An older
 * build drops a false value on its next edit, so it refuses v3 instead.
 * v4 (#515): a maintenance option's set-up (`maintenance`); a v3 build would price it as a repaint, so it refuses v4.
 * v2 and v3 are read as v4 unchanged.
 */
const SCOPE_SCHEMA_VERSION = 4;
const WASH_FLAGS_VERSION = 3;
const SINGLE_SUNDRIES_VERSION = 1;
const LIST_SUNDRIES_VERSION = 2;
/** What a v1 area's one sundries row is called once it is a line (the template's row 63). */
const V1_SUNDRY_DESCRIPTION = 'Sundries';
const emptyScope = () => ({ schemaVersion: SCOPE_SCHEMA_VERSION });
exports.emptyScope = emptyScope;
/** A v1 area's single sundries object as the one line it is in v2; an empty one (0 × 0) becomes none. */
function sundriesFromV1(area) {
    const old = area.sundries;
    if (!old)
        return area;
    const { sundries: _old, ...rest } = area;
    if (!old.items && !old.minutes)
        return rest;
    return { ...rest, sundries: [{ description: V1_SUNDRY_DESCRIPTION, items: old.items, minutes: old.minutes, ...(old.spread && { spread: old.spread }) }] };
}
/** A v1 document as v4: only the sundries change shape, so every price stays the same. It is saved as v4 on the next edit. */
function fromV1(parsed) {
    const areas = parsed.areas && Object.fromEntries(Object.entries(parsed.areas).map(([id, area]) => [id, sundriesFromV1(area)]));
    return { ...parsed, schemaVersion: SCOPE_SCHEMA_VERSION, ...(areas && { areas }) };
}
/** The stored text as a document. Empty = nothing chosen yet; v1 to v3 are read as v4; any other version is refused rather than misread. */
function parseScope(text) {
    if (!text)
        return (0, exports.emptyScope)();
    const parsed = JSON.parse(text);
    if (parsed.schemaVersion === SINGLE_SUNDRIES_VERSION)
        return fromV1(parsed);
    // v2 and v3 have no maintenance block, so each is already a v4 document.
    if (parsed.schemaVersion === LIST_SUNDRIES_VERSION || parsed.schemaVersion === WASH_FLAGS_VERSION) {
        return { ...parsed, schemaVersion: SCOPE_SCHEMA_VERSION };
    }
    if (parsed.schemaVersion !== SCOPE_SCHEMA_VERSION) {
        throw new Error(`This option is in a format this app version can't read (schemaVersion ${String(parsed.schemaVersion)}).`);
    }
    return parsed;
}
const serialiseScope = (scope) => JSON.stringify(scope);
exports.serialiseScope = serialiseScope;
/** Drop a key whose value is undefined, or false unless `keepFalse` names it, and an object left with no keys. */
function pruned(value, keepFalse = []) {
    const entries = Object.entries(value).filter(([k, v]) => v !== undefined && (v !== false || keepFalse.includes(k)));
    return entries.length ? Object.fromEntries(entries) : undefined;
}
/** An area's choices where false means something: off for this option although the site has it on. */
const AREA_OVERRIDES = ['wash', 'washOnly'];
function withArea(scope, areaId, change) {
    const areas = { ...scope.areas };
    const next = pruned(change({ ...areas[areaId] }), AREA_OVERRIDES);
    if (next)
        areas[areaId] = next;
    else
        delete areas[areaId];
    const result = { ...scope, areas };
    if (!Object.keys(areas).length)
        delete result.areas;
    return result;
}
/** Leave an area out of (or back into) this option. */
const setAreaExcluded = (scope, areaId, excluded) => withArea(scope, areaId, (area) => ({ ...area, excluded: excluded || undefined }));
exports.setAreaExcluded = setAreaExcluded;
/** Overtime or foreman % for one area; null clears it (0 %). */
const setAreaShare = (scope, areaId, share, percent) => withArea(scope, areaId, (area) => ({ ...area, [share]: percent ?? undefined }));
exports.setAreaShare = setAreaShare;
/** Change one surface's choices in an area; a choice left empty is dropped. */
function withSurface(scope, areaId, surfaceId, change) {
    return withArea(scope, areaId, (area) => {
        const surfaces = { ...area.surfaces };
        const next = pruned(change({ ...surfaces[surfaceId] }));
        if (next)
            surfaces[surfaceId] = next;
        else
            delete surfaces[surfaceId];
        return { ...area, surfaces: Object.keys(surfaces).length ? surfaces : undefined };
    });
}
/** Coats, production rate or spread rate for this option only; null puts it back to the measure's own value. */
const setSurfaceSetting = (scope, areaId, surfaceId, setting, value) => withSurface(scope, areaId, surfaceId, (s) => ({ ...s, [setting]: value ?? undefined }));
exports.setSurfaceSetting = setSurfaceSetting;
const setSurfaceExcluded = (scope, areaId, surfaceId, excluded) => withSurface(scope, areaId, surfaceId, (s) => ({ ...s, excluded: excluded || undefined }));
exports.setSurfaceExcluded = setSurfaceExcluded;
// ---------- a section's extra rows (the template's rows 63–84) ----------
/** Scaffold moves (count × minutes); null clears the row. Sundries are lines: addAreaLine(…, 'sundries', …). */
const setScaffoldMoves = (scope, areaId, value) => withArea(scope, areaId, (area) => ({ ...area, scaffoldMoves: value && (value.count || value.minutes) ? value : undefined }));
exports.setScaffoldMoves = setScaffoldMoves;
/** The wash m² for this option; 0 is kept (no wash m²), null goes back to the surfaces' total. */
const setWashArea = (scope, areaId, m2) => withArea(scope, areaId, (area) => ({ ...area, washArea: m2 ?? undefined }));
exports.setWashArea = setWashArea;
/**
 * An area's wash for this option (#510): the option's choice, else the site's. Wash only implies Wash, and an
 * area with a wash m² typed at the desk before the Wash tick existed counts as washed. Internal areas: see toSection.
 */
function areaWash(site, choice) {
    const washOnly = choice?.washOnly ?? (choice?.wash !== false && site.washOnly === true);
    return { wash: washOnly || (choice?.wash ?? (site.wash === true || choice?.washArea !== undefined)), washOnly };
}
/** Store the option's wash as the difference from the site's; Wash off also clears a typed wash m². */
function setAreaWashTo(scope, areaId, site, next) {
    const own = areaWash(site, undefined);
    return withArea(scope, areaId, (area) => ({
        ...area,
        wash: next.wash === own.wash ? undefined : next.wash,
        washOnly: next.washOnly === own.washOnly ? undefined : next.washOnly,
        ...(!next.wash && { washArea: undefined }),
    }));
}
/** Wash on or off for this option; off turns Wash only off too. */
const setAreaWash = (scope, areaId, site, on) => setAreaWashTo(scope, areaId, site, { wash: on, washOnly: on && areaWash(site, scope.areas?.[areaId]).washOnly });
exports.setAreaWash = setAreaWash;
/** Wash only on (which turns Wash on) or off (Wash stays on) for this option. */
const setAreaWashOnly = (scope, areaId, site, on) => setAreaWashTo(scope, areaId, site, { wash: on || areaWash(site, scope.areas?.[areaId]).wash, washOnly: on });
exports.setAreaWashOnly = setAreaWashOnly;
/** This area's own wash rate (m²/h); null goes back to the option's. */
const setWashRate = (scope, areaId, rate) => withArea(scope, areaId, (area) => ({ ...area, washRate: rate || undefined }));
exports.setWashRate = setWashRate;
/** This area's own consumables rate ($/L sell); null goes back to the option's. */
const setConsumablesRate = (scope, areaId, rate) => withArea(scope, areaId, (area) => ({ ...area, consumablesRate: rate ?? undefined }));
exports.setConsumablesRate = setConsumablesRate;
function withLines(scope, areaId, list, change) {
    return withArea(scope, areaId, (area) => {
        const lines = change([...(area[list] ?? [])]);
        return { ...area, [list]: lines.length ? lines : undefined };
    });
}
const addAreaLine = (scope, areaId, list, line) => withLines(scope, areaId, list, (lines) => [...lines, line]);
exports.addAreaLine = addAreaLine;
/** Change one line; a typed value set to null is removed (back to the hire list's rate). */
const updateAreaLine = (scope, areaId, list, index, change) => withLines(scope, areaId, list, (lines) => lines.map((line, i) => (i === index ? Object.fromEntries(Object.entries({ ...line, ...change }).filter(([, v]) => v !== null && v !== undefined)) : line)));
exports.updateAreaLine = updateAreaLine;
const removeAreaLine = (scope, areaId, list, index) => withLines(scope, areaId, list, (lines) => lines.filter((_, i) => i !== index));
exports.removeAreaLine = removeAreaLine;
// ---------- the option's paint list ----------
/** The list in force: the option's own, else the generic paints it starts from. */
const paintsOf = (scope, generic) => (scope.paints?.length ? scope.paints : generic);
exports.paintsOf = paintsOf;
/** Add a paint (a price-book product, by its id); the list starts from the generics the first time. */
function addPaint(scope, generic, paint) {
    const paints = (0, exports.paintsOf)(scope, generic);
    if (paints.some((p) => p.key === paint.key))
        return scope;
    return { ...scope, paints: [...paints, paint] };
}
/** Remove a paint, and point every surface and paint system that used it back at the first (standard) paint. The first can't be removed. */
function removePaint(scope, generic, key) {
    const paints = (0, exports.paintsOf)(scope, generic);
    if (paints[0]?.key === key)
        return scope;
    let next = { ...scope, paints: paints.filter((p) => p.key !== key) };
    const offPaint = (x) => {
        if (x.paint !== key)
            return x;
        const { paint: _removed, ...rest } = x;
        return rest;
    };
    for (const [areaId, area] of Object.entries(next.areas ?? {})) {
        for (const [surfaceId, choice] of Object.entries(area.surfaces ?? {})) {
            if (choice.paint === key || choice.systems?.some((x) => x.paint === key)) {
                next = withSurface(next, areaId, surfaceId, (s) => ({ ...offPaint(s), systems: s.systems?.map(offPaint) }));
            }
        }
    }
    return next;
}
/**
 * A paint key as stored for this option: the first paint (or null) is the default and stores nothing.
 * Choosing another paint keeps the option's own copy of the paint list, so later changes to the generics don't move it.
 */
function storedPaint(scope, generic, key) {
    const paints = (0, exports.paintsOf)(scope, generic);
    const isDefault = key === null || paints[0]?.key === key;
    return { scope: isDefault || scope.paints?.length ? scope : { ...scope, paints }, paint: isDefault ? undefined : (key ?? undefined) };
}
/** A surface's paint for this option; the first paint (or null) is the default and stores nothing. */
function setSurfacePaint(scope, generic, areaId, surfaceId, key) {
    const stored = storedPaint(scope, generic, key);
    return withSurface(stored.scope, areaId, surfaceId, (s) => ({ ...s, paint: stored.paint }));
}
/** Change the sell or cost half of one rate for this option; null puts it back to the branch's / setting's value. */
function setRate(scope, key, half, value) {
    const rates = { ...scope.rates };
    const next = pruned({ ...rates[key], [half]: value ?? undefined });
    if (next)
        rates[key] = next;
    else
        delete rates[key];
    const result = { ...scope, rates };
    if (!Object.keys(rates).length)
        delete result.rates;
    return result;
}
